// Bridge: TabSet ⇄ single-leaf PaneTree (Step-2 migration seam).
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition).
//
// The single-leaf-equivalence step (IDD 6ade2868) stores a PaneTree but keeps
// the legacy render + fold path speaking TabSet. This lossless adapter pair is
// that seam: everything-is-a-tab lives in the STORED leaf (the conversation is
// a real ConversationTab — no boolean), while the derived TabSet reconstructs
// the boolean as a VIEW for the unchanged callers. Canonical leaf order
// [home, ...resources, conversation?] keeps the strip pixel-identical. Later
// steps move the folds + render onto the leaf and retire this pair.

import type {
    KnowledgeSeed,
    QualityTab,
    ResourceTab,
    SearchSeed,
    TabSet,
} from "../types.js";
import { activeToKey, keyToActive } from "./keys.js";
import { focusedLeaf, mapLeaf, seedPaneTree } from "./tree.js";
import type {
    ConversationTab,
    HomeTab,
    KnowledgeTab,
    Leaf,
    PaneId,
    PaneTree,
    ProjectsTab,
    SearchTab,
    SettingsTab,
    StudioTab,
} from "./types.js";

// The sole leaf's id while the tree is single-leaf (no split UI yet). Splits
// (a later step) mint real per-pane ids from the shell; until then every
// workspace's tree is one leaf under this stable label.
export const MAIN_PANE: PaneId = "main";

// A single-leaf tree from a TabSet: home first, the open resources, then the
// trailing tabs — conversations, then Settings last. The active tab
// maps across unchanged.
export const paneTreeFromTabSet = (tabs: TabSet): PaneTree => {
    const conversations: readonly ConversationTab[] =
        tabs.conversationsOpen.map((id) => ({ type: "conversation", id }));
    const settings: readonly SettingsTab[] = tabs.settingsOpen
        ? [{ type: "settings" }]
        : [];
    const home: readonly HomeTab[] = tabs.homeOpen ? [{ type: "home" }] : [];
    const studio: readonly StudioTab[] = tabs.studioOpen
        ? [{ type: "studio" }]
        : [];
    const projects: readonly ProjectsTab[] = tabs.projectsOpen
        ? [{ type: "projects" }]
        : [];
    const knowledge: readonly KnowledgeTab[] = tabs.knowledgeOpen
        ? [
              {
                  type: "knowledge",
                  ...knowledgeSeedOf(
                      tabs.active.type === "knowledge" ? tabs.active : {},
                  ),
              },
          ]
        : [];
    // Fresh tree from a TabSet: the ONLY facet source is the active tab (a
    // backgrounded Search tab's facet isn't carried in a TabSet), so an
    // inactive-but-open Search reconstructs unfiltered here — acceptable, this
    // path (reconcile / retitle) never targets a backgrounded Search facet.
    const search: readonly SearchTab[] = tabs.searchOpen
        ? [
              {
                  type: "search",
                  ...seedOf(tabs.active.type === "search" ? tabs.active : {}),
              },
          ]
        : [];
    return {
        root: {
            type: "leaf",
            id: MAIN_PANE,
            // Canonical order: the leading Home + Search singletons, then the
            // open resources, then the quality reports (after the resources
            // they report on), then the conversations (nascent or durable),
            // then the trailing settings singleton — exactly the strip order.
            tabs: [
                ...home,
                ...studio,
                ...projects,
                ...knowledge,
                ...search,
                ...tabs.open,
                ...tabs.qualityOpen,
                ...conversations,
                ...settings,
            ],
            preview: null,
            active: activeToKey(tabs.active),
        },
        focused: MAIN_PANE,
    };
};

// The fresh, never-visited tree — a single leaf holding one fresh hero.
export const emptyPaneTree: PaneTree = seedPaneTree(MAIN_PANE);

// The TabSet view of a leaf: the open resources (with their dirty marks), the
// conversation membership as a boolean, and the active tab mapped back.
// The Search MEMBER a leaf holds: its seed (facet, selection, target, query)
// is the source of truth the active pointer only references (F48), so a
// re-activated (previously backgrounded) Search tab keeps it.
const searchMemberOf = (leaf: Leaf): SearchTab | undefined =>
    leaf.tabs.find((t): t is SearchTab => t.type === "search");

// The seed fields of a Search tab or member, listed once.
const seedOf = (s: SearchSeed): SearchSeed => ({
    kind: s.kind,
    selectedId: s.selectedId,
    target: s.target,
    query: s.query,
});

// The Knowledge bank MEMBER a leaf holds, and its seed fields listed once.
const knowledgeMemberOf = (leaf: Leaf): KnowledgeTab | undefined =>
    leaf.tabs.find((t): t is KnowledgeTab => t.type === "knowledge");
const knowledgeSeedOf = (s: KnowledgeSeed): KnowledgeSeed => ({
    selectedId: s.selectedId,
    target: s.target,
});

const tabSetOfLeaf = (leaf: Leaf): TabSet => ({
    open: leaf.tabs.filter((t): t is ResourceTab => t.type === "resource"),
    qualityOpen: leaf.tabs.filter((t): t is QualityTab => t.type === "quality"),
    conversationsOpen: leaf.tabs
        .filter((t): t is ConversationTab => t.type === "conversation")
        .map((t) => t.id),
    settingsOpen: leaf.tabs.some((t) => t.type === "settings"),
    homeOpen: leaf.tabs.some((t) => t.type === "home"),
    studioOpen: leaf.tabs.some((t) => t.type === "studio"),
    projectsOpen: leaf.tabs.some((t) => t.type === "projects"),
    knowledgeOpen: leaf.tabs.some((t) => t.type === "knowledge"),
    searchOpen: leaf.tabs.some((t) => t.type === "search"),
    // Re-hydrate the active Search facet (and the Knowledge bank's selection)
    // off the MEMBER, so a re-activated (previously backgrounded) tab keeps
    // its seed.
    active:
        leaf.active.type === "search"
            ? { type: "search", ...seedOf(searchMemberOf(leaf) ?? {}) }
            : leaf.active.type === "knowledge"
              ? {
                    type: "knowledge",
                    ...knowledgeSeedOf(knowledgeMemberOf(leaf) ?? {}),
                }
              : keyToActive(leaf.active),
});

// The TabSet view of the tree's focused (single) leaf.
export const tabSetOfPaneTree = (tree: PaneTree): TabSet =>
    tabSetOfLeaf(focusedLeaf(tree));

// The exposed TabSet view of a specific leaf — the render path reads each
// pane's strip from this (the per-pane ResourceTabs input).
export const tabSetOf = (leaf: Leaf): TabSet => tabSetOfLeaf(leaf);

// Write a TabSet back into the FOCUSED leaf ONLY, preserving the rest of the
// tree (the multi-leaf-safe successor to `paneTreeFromTabSet` for the fold
// path). The focused leaf's HOME membership is preserved — a secondary leaf
// (no home) never gains one — so the primary-pane-only home invariant holds.
export const withFocusedLeafTabs = (tree: PaneTree, tabs: TabSet): PaneTree => {
    const root = mapLeaf(tree.root, tree.focused, (leaf) => {
        const conversations: readonly ConversationTab[] =
            tabs.conversationsOpen.map((id) => ({ type: "conversation", id }));
        const settings: readonly SettingsTab[] = tabs.settingsOpen
            ? [{ type: "settings" }]
            : [];
        const home: readonly HomeTab[] = tabs.homeOpen
            ? [{ type: "home" }]
            : [];
        const studio: readonly StudioTab[] = tabs.studioOpen
            ? [{ type: "studio" }]
            : [];
        const projects: readonly ProjectsTab[] = tabs.projectsOpen
            ? [{ type: "projects" }]
            : [];
        const knowledge: readonly KnowledgeTab[] = tabs.knowledgeOpen
            ? [
                  {
                      type: "knowledge",
                      ...knowledgeSeedOf(
                          tabs.active.type === "knowledge"
                              ? tabs.active
                              : (knowledgeMemberOf(leaf) ?? {}),
                      ),
                  },
              ]
            : [];
        // Preserve the Search seed facet across the write-back: from the active
        // tab when Search is active, else from the prior leaf's Search member so
        // a backgrounded Search keeps its facet (the write path a resource-open
        // beside an open Search takes).
        const search: readonly SearchTab[] = tabs.searchOpen
            ? [
                  {
                      type: "search",
                      ...seedOf(
                          tabs.active.type === "search"
                              ? tabs.active
                              : (searchMemberOf(leaf) ?? {}),
                      ),
                  },
              ]
            : [];
        // Keep the invariant across the TabSet bridge: a preview whose ref has
        // become a KEPT resource tab leaves the slot (never both). The slot is
        // otherwise preserved — the TabSet view carries no preview of its own.
        const prev = leaf.preview;
        const preview =
            prev !== null && tabs.open.some((o) => o.resource.id === prev.id)
                ? null
                : prev;
        return {
            ...leaf,
            tabs: [
                ...home,
                ...studio,
                ...projects,
                ...knowledge,
                ...search,
                ...tabs.open,
                ...tabs.qualityOpen,
                ...conversations,
                ...settings,
            ],
            preview,
            active: activeToKey(tabs.active),
        };
    });
    return root === null || root === tree.root ? tree : { ...tree, root };
};

// Whether a tree carries no working-set information beyond the default (a
// single home-only leaf) — the persistence skip, mirroring the old
// "nothing open" filter. A GLANCE counts as working-set info (D7: a preview
// persists), so a home-only leaf holding a preview is NOT empty.
export const isEmptyPaneTree = (tree: PaneTree): boolean =>
    tree.root.type === "leaf" &&
    tree.root.tabs.length === 1 &&
    tree.root.tabs[0]?.type === "conversation" &&
    tree.root.preview === null;
